import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { saveAs } from 'file-saver';

import type { RentManagerConfig, StructureBilling, UnitBilling } from '@/Structures/types';
import { convertArrayToCsvString, naturalCompare } from '../util';

dayjs.extend(utc);
dayjs.extend(timezone);

export type PmsExportRow = {
  'Property Short Name': string;
  'Unit Name': string;
  Amount: string;
  Date: string;
  'Charge Type': string;
  Comment: string;
  Reference: string;
};

export type MidCycleWarning = {
  unitName: string;
  invoiceCount: number;
};

/**
 * Detect units with mid-cycle tenant changes (multiple invoices for one unit).
 */
export function detectMidCycleChanges(units: UnitBilling[], invoices: any[]): MidCycleWarning[] {
  const warnings: MidCycleWarning[] = [];
  for (const unit of units) {
    const unitInvoices = invoices.filter((inv: any) => inv.unitId === unit.id);
    if (unitInvoices.length > 1) {
      warnings.push({
        unitName: unit.name,
        invoiceCount: unitInvoices.length,
      });
    }
  }
  return warnings;
}

/**
 * Build Rent Manager export rows from enriched billing data.
 * Only includes billable units.
 *
 * @param unitPmsNames - Map of unit ID → PMS unit name (from units.pms_unit_name column)
 */
export function buildRentManagerExportRows(
  bill: StructureBilling,
  enrichedUnits: UnitBilling[],
  pmsConfig: RentManagerConfig,
  tz: string,
  unitPmsNames: Record<string, string>,
): PmsExportRow[] {
  const formattedDate = dayjs.unix(bill.end_time).tz(tz).format('MM/DD/YYYY');
  const startDate = dayjs.unix(bill.start_time).tz(tz).format('MM/DD/YYYY');
  const endDate = formattedDate;

  return enrichedUnits
    .filter((unit) => {
      const isBillable = unit.is_billable ?? true;
      const flags = unit.unit_flags ?? [];
      return isBillable && !flags.includes('COMMON_AREA') && !flags.includes('NON_BILLABLE');
    })
    .sort((a, b) => {
      const nameA = unitPmsNames[a.id] || a.name;
      const nameB = unitPmsNames[b.id] || b.name;
      return naturalCompare(nameA, nameB);
    })
    .map((unit) => {
      // Total charge = water usage cost + allocated fees
      const amount = (unit.unit_cost_meter ?? 0) + (unit.unit_cost ?? 0);
      const unitName = unitPmsNames[unit.id] || unit.name;

      return {
        'Property Short Name': pmsConfig.property_short_name,
        'Unit Name': unitName,
        Amount: amount.toFixed(2),
        Date: formattedDate,
        'Charge Type': pmsConfig.default_charge_type || '',
        Comment: `Water charges ${startDate} - ${endDate}`,
        Reference: bill.billing_id,
      };
    });
}

/**
 * Generate and download a Rent Manager CSV export.
 *
 * @param unitPmsNames - Map of unit ID → PMS unit name (from units.pms_unit_name column)
 */
export async function downloadRentManagerCsv(
  bill: StructureBilling,
  enrichedUnits: UnitBilling[],
  pmsConfig: RentManagerConfig,
  tz: string,
  structureName: string,
  unitPmsNames: Record<string, string>,
): Promise<void> {
  const rows = buildRentManagerExportRows(bill, enrichedUnits, pmsConfig, tz, unitPmsNames);

  if (rows.length === 0) {
    throw new Error('No billable units to export');
  }

  const csvString = await convertArrayToCsvString(rows);
  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8' });

  const startDate = dayjs.unix(bill.start_time).tz(tz).format('YYYY-MM-DD');
  const endDate = dayjs.unix(bill.end_time).tz(tz).format('YYYY-MM-DD');
  const safeName = structureName.replace(/[^a-zA-Z0-9]/g, '_');
  const filename = `RM_Export_${safeName}_${startDate}_${endDate}.csv`;

  saveAs(blob, filename);
}
